/**
 * Playlist Hooks
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { playlistService } from '../services/playlists';
import { trackService } from '../services/tracks';
import type {
  PlaylistCreate,
  PlaylistUpdate,
  AddTracksRequest,
  RemoveTracksRequest,
  PaginationParams,
  PlaylistDetail,
  PlaylistListResponse,
} from '../types';

export const playlistKeys = {
  all: ['playlists'] as const,
  lists: () => [...playlistKeys.all, 'list'] as const,
  list: (params?: PaginationParams) => [...playlistKeys.lists(), params] as const,
  infinite: () => [...playlistKeys.all, 'infinite'] as const,
  details: () => [...playlistKeys.all, 'detail'] as const,
  detail: (id: string) => [...playlistKeys.details(), id] as const,
};

/**
 * Hook to list user's playlists
 */
export function usePlaylists(params?: PaginationParams) {
  return useQuery({
    queryKey: playlistKeys.list(params),
    queryFn: () => playlistService.list(params),
  });
}

/**
 * Infinite query hook for playlist pagination.
 *
 * Automatically fetches next page when user scrolls to bottom.
 * Uses React Query's built-in caching and deduplication.
 */
export function useInfinitePlaylists(limit: number = 50, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: playlistKeys.infinite(),
    queryFn: ({ pageParam = 0 }) =>
      playlistService.list({ limit, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: PlaylistListResponse, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.playlists.length, 0);
      const hasMore = loadedCount < lastPage.total;
      return hasMore ? loadedCount : undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes (matches backend cache)
    gcTime: 1000 * 60 * 15, // 15 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to get a single playlist with all tracks
 * Special handling for Liked Songs pseudo-playlist
 */
export function usePlaylist(
  playlistId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: playlistKeys.detail(playlistId!),
    queryFn: async (): Promise<PlaylistDetail> => {
      // Special handling for Liked Songs
      if (playlistId === 'liked-songs') {
        const likedData = await trackService.getLiked({ limit: 50, offset: 0 });

        return {
          id: 'liked-songs',
          name: 'Liked Songs',
          description: 'Your saved tracks',
          public: false,
          collaborative: false,
          owner: {
            id: 'spotify',
            displayName: 'Spotify',
            uri: 'spotify:user:spotify',
          },
          snapshotId: '',
          uri: 'spotify:collection:tracks',
          images: [],
          tracks: {
            total: likedData.tracks.length,
            items: likedData.tracks.map((track) => ({
              track,
              addedAt: new Date().toISOString(),
              addedBy: { id: 'spotify', displayName: 'You', uri: 'spotify:user:spotify' },
            })),
          },
        };
      }

      // Regular playlist
      return playlistService.get(playlistId!);
    },
    enabled: options?.enabled !== undefined ? options.enabled && !!playlistId : !!playlistId,
  });
}

/**
 * Hook to create a new playlist
 */
export function useCreatePlaylist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PlaylistCreate) => playlistService.create(data),
    onSuccess: () => {
      // Invalidate all playlist queries to refetch
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
    },
  });
}

/**
 * Hook to update playlist metadata
 */
export function useUpdatePlaylist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playlistId, data }: { playlistId: string; data: PlaylistUpdate }) =>
      playlistService.update(playlistId, data),
    onSuccess: (_, { playlistId }) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
    },
  });
}

/**
 * Hook to delete a playlist with optimistic update
 */
export function useDeletePlaylist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (playlistId: string) => playlistService.delete(playlistId),
    onMutate: async (playlistId: string) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: playlistKeys.infinite() });

      // Snapshot current state
      const previousData = queryClient.getQueryData(playlistKeys.infinite());

      // Optimistically remove playlist from cache
      queryClient.setQueryData(playlistKeys.infinite(), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: PlaylistListResponse) => ({
            ...page,
            playlists: page.playlists.filter((p) => p.id !== playlistId),
            total: page.total - 1,
          })),
        };
      });

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(playlistKeys.infinite(), context.previousData);
      }
    },
    onSettled: (_, __, playlistId) => {
      // Remove detail cache
      queryClient.removeQueries({ queryKey: playlistKeys.detail(playlistId) });
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
    },
  });
}

/**
 * Hook to add tracks to a playlist
 */
export function useAddTracks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playlistId, data }: { playlistId: string; data: AddTracksRequest }) =>
      playlistService.addTracks(playlistId, data),
    onSuccess: (_, { playlistId }) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      queryClient.invalidateQueries({ queryKey: playlistKeys.all }); // Update track count in sidebar
    },
  });
}

/**
 * Hook to remove tracks from a playlist
 */
export function useRemoveTracks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playlistId, data }: { playlistId: string; data: RemoveTracksRequest }) =>
      playlistService.removeTracks(playlistId, data),
    onSuccess: (_, { playlistId }) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      queryClient.invalidateQueries({ queryKey: playlistKeys.all }); // Update track count in sidebar
    },
  });
}

/**
 * Hook to prefetch playlist on hover
 */
export function usePrefetchPlaylist() {
  const queryClient = useQueryClient();

  return (playlistId: string) => {
    // Skip prefetch for special collections (e.g., Liked Songs)
    // These are fetched via different endpoints
    if (playlistId === 'liked-songs') {
      return;
    }

    queryClient.prefetchQuery({
      queryKey: playlistKeys.detail(playlistId),
      queryFn: () => playlistService.get(playlistId),
      staleTime: 60_000, // 1 minute
    });
  };
}
